import {
  Category,
  CategoryDiagnosisType,
  Diagnosis,
  DiagnosisInput,
  DiagnosisTypeEnum,
  ModernIcd10Description,
  OxygenMeasurements,
  PlanOfCare,
} from "../__generated__/graphql";

import { camelCase } from "../utils/formatting";

const OXYGEN_MEASUREMENT_COLUMNS = new Set([
  "oxygen_spo2_measurements",
  "oxygen_po2_measurements",
]);

// Plan-of-care fields whose summary cell should be omitted entirely when the
// value is blank, rather than rendering an empty column. Matched by
// `field.display` (the human-readable label). Centralized here so all three
// summary renderers (provider Order Summary, ops Original Order, SWO PDF) stay
// in sync.
const HIDE_WHEN_EMPTY_FIELDS = new Set([
  "Oxygen Per Minute",
  "SpO2 Percent",
  "PO2 mm Hg",
  "Oxygen Types",
  "Continuous Options",
  "SpO2 Measurements",
  "PO2 Measurements",
  "Respiratory Testing Date",
  "Hypoxia Improves With O2 Confirmed",
  // Historical orders predate this field; hide the column when empty so their
  // summaries are unchanged. New orders always have a value (it's required).
  "Portable oxygen type",
]);

// Flattens an OxygenMeasurements wrapper into a single human-readable line.
// Returns "" when every per-Usage reading is null/absent, so a row with no
// measurements yet renders blank instead of "[object Object]".
const formatOxygenMeasurements = (measurements: OxygenMeasurements): string => {
  const groups: string[] = [];

  if (measurements.continuous?.roomAir != null) {
    groups.push(`Continuous: ${measurements.continuous.roomAir}`);
  }

  const nocturnal = measurements.nocturnal;
  if (nocturnal) {
    const parts: string[] = [];
    if (nocturnal.awake != null) parts.push(`${nocturnal.awake} awake`);
    if (nocturnal.asleep != null) parts.push(`${nocturnal.asleep} asleep`);
    if (nocturnal.baselineAsleep != null) {
      parts.push(`${nocturnal.baselineAsleep} baseline asleep`);
    }
    if (parts.length > 0) groups.push(`Nocturnal: ${parts.join(" / ")}`);
  }

  const exercise = measurements.exercise;
  if (exercise) {
    const parts: string[] = [];
    if (exercise.roomAirPre != null) parts.push(`${exercise.roomAirPre} pre`);
    if (exercise.roomAirDuring != null) {
      parts.push(`${exercise.roomAirDuring} during`);
    }
    if (exercise.onO2Post != null) parts.push(`${exercise.onO2Post} on-O2`);
    if (parts.length > 0) groups.push(`Exercise: ${parts.join(" / ")}`);
  }

  return groups.join(" | ");
};

/**
 * Generates the display values for the Plan of Care fields for a given category.
 *
 * @param {Category} category - The category for which the Plan of Care displays are generated.
 * @param {PlanOfCare[]} planOfCares - An array of Plan of Care objects to filter and display values from.
 * @returns {string[]} An array of strings representing the display values for the Plan of Care fields.
 */
export const generatePlanOfCareDisplaysForCategory = (
  category: Category,
  planOfCares: PlanOfCare[],
): string[] => {
  if (!planOfCares) {
    return [];
  }
  const planOfCareForCategory = planOfCares.filter(
    (planOfCare) => planOfCare.categoryId === category.id,
  )?.[0];
  return category.planOfCareFields
    .filter((field) => field.visibleInSummary)
    .map((field) => {
      if (!planOfCareForCategory) {
        return "";
      }
      const planOfCareValue =
        planOfCareForCategory[camelCase(field.columnName) as keyof PlanOfCare];

      // Handle undefined or null values explicitly
      if (planOfCareValue === undefined || planOfCareValue === null) {
        return "";
      }

      if (typeof planOfCareValue === "boolean") {
        return planOfCareValue ? "Yes" : "No";
      }

      // Handle arrays by joining with ", " instead of using toString() which uses ","
      if (Array.isArray(planOfCareValue)) {
        return planOfCareValue.join(", ");
      }

      if (OXYGEN_MEASUREMENT_COLUMNS.has(field.columnName)) {
        return formatOxygenMeasurements(planOfCareValue as OxygenMeasurements);
      }

      return planOfCareValue?.toString() || "";
    });
};

/**
 * Generates paired header/display tuples for the Plan of Care section of a
 * category, with hide-when-empty filtering applied. Fields in
 * HIDE_WHEN_EMPTY_FIELDS are omitted from the result when their display is
 * blank, keeping summary tables free of empty columns for unanswered Oxygen
 * sub-fields.
 *
 * Use this in summary renderers instead of pairing
 * `category.planOfCareFields.filter(visibleInSummary)` with
 * `generatePlanOfCareDisplaysForCategory` by hand.
 */
export const generatePlanOfCareHeadersAndDisplaysForCategory = (
  category: Category,
  planOfCares: PlanOfCare[],
): { header: string; display: string }[] => {
  const displays = generatePlanOfCareDisplaysForCategory(category, planOfCares);
  const visibleFields = category.planOfCareFields.filter(
    (field) => field.visibleInSummary,
  );
  return visibleFields
    .map((field, index) => ({
      header: field.display,
      display: displays[index] ?? "",
    }))
    .filter(({ header, display }) =>
      HIDE_WHEN_EMPTY_FIELDS.has(header) ? display.trim() !== "" : true,
    );
};

/**
 * Generates the display values for the diagnoses associated with a specific CategoryDiagnosisType.
 *
 * @param {Diagnosis[]} diagnoses - An array of Diagnosis objects to filter and display values from.
 * @param {CategoryDiagnosisType} categoryDiagnosisType - The CategoryDiagnosisType for which the displays are generated.
 * @param {ModernIcd10Description[]} defaultIcd10DescriptionsForCategory - An array of default ICD10 descriptions for the category.
 * @returns {string[][]} A 2D array of strings where each nested array represents the display values for a single diagnosis.
 */
export const generateCategoryDiagnosisTypeDisplays = (
  diagnoses: Diagnosis[],
  categoryDiagnosisType: CategoryDiagnosisType,
  defaultIcd10DescriptionsForCategory: ModernIcd10Description[],
): string[][] => {
  const filteredDiagnoses = diagnoses.filter(
    (d) => d.categoryDiagnosisTypeId === categoryDiagnosisType.id,
  );
  const diagnosisType = categoryDiagnosisType.diagnosisType;
  return filteredDiagnoses.map((diagnosis) => {
    return diagnosisType.diagnosisFields.map((field) => {
      const value = diagnosis[camelCase(field.columnName) as keyof Diagnosis];

      if (diagnosisType.type === DiagnosisTypeEnum.Icd10Diagnosis) {
        if (field.columnName === "icd10_description_id") {
          // True custom ICD10 Diagnosis or structured diagnosis which is not default for category
          if (diagnosis.customIcd10Description) {
            return diagnosis.customIcd10Code
              ? `${diagnosis.customIcd10Code} - ${diagnosis.customIcd10Description}`
              : diagnosis.customIcd10Description;
          }
          // Default ICD10 Diagnosis
          if (value && !diagnosis.customIcd10Description) {
            const defaultIcd10Description =
              defaultIcd10DescriptionsForCategory.find(
                (defaultIcd10Description) =>
                  defaultIcd10Description.id == value,
              );
            return defaultIcd10Description
              ? `${defaultIcd10Description?.code} - ${defaultIcd10Description?.description}`
              : "";
          }
        }
      }
      // TODO: Implement return when we add new diagnosis types
      return "";
    });
  });
};

/**
 * Generates the display values for the diagnoses associated with a specific CategoryDiagnosisType.
 *
 * @param {Diagnosis[]} diagnoses - An array of Diagnosis objects to filter and display values from.
 * @param {CategoryDiagnosisType} categoryDiagnosisType - The CategoryDiagnosisType for which the displays are generated.
 * @param {ModernIcd10Description[]} defaultIcd10DescriptionsForCategory - An array of default ICD10 descriptions for the category.
 * @returns {{ code: string; description: string }[][]} A 2D array of strings where each nested array represents the display values for a single diagnosis.
 */
export const generateCategoryDiagnosisTypeInfo = (
  diagnoses: DiagnosisInput[] | undefined | null,
  categoryDiagnosisType: CategoryDiagnosisType | undefined | null,
  defaultIcd10DescriptionsForCategory: ModernIcd10Description[],
): { code: string; description: string }[][] => {
  if (
    diagnoses == null ||
    categoryDiagnosisType == null ||
    categoryDiagnosisType.diagnosisType == null
  ) {
    return [];
  }

  const filteredDiagnoses = diagnoses.filter(
    (d) => d.categoryDiagnosisTypeId === categoryDiagnosisType.id,
  );
  const diagnosisType = categoryDiagnosisType.diagnosisType;
  return filteredDiagnoses.map((diagnosis) => {
    return diagnosisType.diagnosisFields.map((field) => {
      const value =
        diagnosis[camelCase(field.columnName) as keyof DiagnosisInput];

      if (diagnosisType.type === DiagnosisTypeEnum.Icd10Diagnosis) {
        if (field.columnName === "icd10_description_id") {
          // True custom ICD10 Diagnosis or structured diagnosis which is not default for category
          if (diagnosis.customIcd10Description) {
            return {
              code: diagnosis.customIcd10Code ?? "",
              description: diagnosis.customIcd10Description,
            };
          }
          // Default ICD10 Diagnosis
          if (value && !diagnosis.customIcd10Description) {
            const defaultIcd10Description =
              defaultIcd10DescriptionsForCategory.find(
                (defaultIcd10Description) =>
                  defaultIcd10Description.id == value,
              );
            return {
              code: defaultIcd10Description?.code || "",
              description: defaultIcd10Description?.description || "",
            };
          }
        }
      }
      // TODO: Implement return when we add new diagnosis types
      return {
        code: "",
        description: "",
      };
    });
  });
};
